import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface NavigationEntry {
    url: string;
    timestamp: string;
}

/** Same error key won't be sent again within this window */
const RATE_LIMIT_WINDOW_MS = 5000;
/** Drop new events when this many sends are already in-flight (backend is down) */
const MAX_QUEUE_SIZE = 10;

@Injectable({ providedIn: 'root' })
export class ErrorTrackingService {
    private readonly MAX_NAV_HISTORY = 15;
    private navigationFlow: NavigationEntry[] = [];

    /** Re-entrancy guard: prevents logException from triggering itself */
    private isSendingException = false;

    /** Deduplication map: errorKey → timestamp of last send */
    private readonly recentErrors = new Map<string, number>();

    /** Tracks concurrent in-flight fetch calls */
    private inFlightCount = 0;

    constructor(private router: Router) {
        this.trackNavigation();
    }

    private trackNavigation(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: any) => {
                this.navigationFlow.push({
                    url: event.urlAfterRedirects,
                    timestamp: new Date().toISOString()
                });
                if (this.navigationFlow.length > this.MAX_NAV_HISTORY) {
                    this.navigationFlow.shift();
                }
            });
    }

    private getUserEmail(): string {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            return user?.email || 'anonymous';
        } catch {
            return 'anonymous';
        }
    }

    private getEnvironment(): string {
        return environment.production ? 'production' : 'development';
    }

    private getSeverity(statusCode: number): string {
        if (statusCode >= 500 || statusCode === 0) return 'High';
        if (statusCode >= 400) return 'Medium';
        return 'Low';
    }

    /** Returns true if this errorKey was sent recently (rate-limit exceeded). */
    private isDuplicate(key: string): boolean {
        const now = Date.now();
        const last = this.recentErrors.get(key);
        if (last && now - last < RATE_LIMIT_WINDOW_MS) {
            return true;
        }
        this.recentErrors.set(key, now);
        // Prune old entries to avoid memory leak
        if (this.recentErrors.size > 200) {
            const cutoff = now - RATE_LIMIT_WINDOW_MS;
            this.recentErrors.forEach((ts, k) => {
                if (ts < cutoff) this.recentErrors.delete(k);
            });
        }
        return false;
    }

    logHttpError(error: HttpErrorResponse, request: HttpRequest<any>): void {
        const key = `http:${error.status}:${request.method}:${request.url}`;
        if (this.isDuplicate(key) || this.inFlightCount >= MAX_QUEUE_SIZE) return;

        const payload = {
            service: 'angular-frontend',
            source: 'Frontend',
            type: 'HttpErrorResponse',
            message: error.message,
            stack: error.error?.stack || null,
            errorCode: `ERR_HTTP_${error.status}`,
            severity: this.getSeverity(error.status),
            environment: this.getEnvironment(),
            user: this.getUserEmail(),
            navigationFlow: [...this.navigationFlow],
            request: {
                method: request.method,
                url: request.url,
                headers: {
                    'Content-Type': request.headers.get('Content-Type') || 'application/json',
                    ...(request.headers.has('Authorization') ? { Authorization: 'Bearer ***' } : {})
                },
                body: request.body || null
            },
            response: {
                statusCode: error.status,
                body: error.error || null
            }
        };

        this.send(`${environment.errorTrackingUrl}/api/error/new`, payload);
    }

    logException(error: any): void {
        // Re-entrancy guard: if this method itself caused an error, do nothing
        if (this.isSendingException) return;
        if (this.inFlightCount >= MAX_QUEUE_SIZE) return;

        const originalError = error?.originalError || error?.rejection || error;
        const msg: string = originalError?.message || String(originalError);
        const key = `exception:${originalError?.name}:${msg.slice(0, 100)}`;
        if (this.isDuplicate(key)) return;

        this.isSendingException = true;

        try {
            const payload = {
                service: 'angular-frontend',
                source: 'Frontend',
                type: originalError?.name || 'Error',
                message: msg,
                stack: originalError?.stack || null,
                severity: 'Critical',
                environment: this.getEnvironment(),
                user: this.getUserEmail(),
                navigationFlow: [...this.navigationFlow],
                request: {
                    method: 'GET',
                    url: window.location.pathname,
                    headers: {
                        'User-Agent': navigator.userAgent
                    },
                    body: null
                },
                response: {
                    statusCode: 200,
                    body: null
                }
            };

            this.send(`${environment.errorTrackingUrl}/api/exception/new`, payload);
        } finally {
            // Always release the guard so future (different) errors can be reported
            this.isSendingException = false;
        }
    }

    private send(url: string, payload: any): void {
        this.inFlightCount++;
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': 'cls_ingest_fuwion_2026_angular'
            },
            body: JSON.stringify(payload)
        })
            .catch(() => { /* swallow — logging failed, nothing more to do */ })
            .finally(() => { this.inFlightCount--; });
    }
}
